import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "./api-client";

/**
 * The client half of the owner-only subscriber surface
 * (`Api::V1::Admin::EmailSubscribersController`): read the list, remove an
 * address from it.
 *
 * The credential never appears here. Both endpoints authenticate by the
 * operator session cookie, which the api-client attaches through
 * `credentials: "same-origin"` — so an expired session is simply a 401 these
 * functions let through as a rejection, and the page above decides what to say
 * about it.
 */

const ADMIN_SUBSCRIBERS_URL = "/api/v1/admin/email_subscribers";

/**
 * The cache prefix every page's key extends, exported so the delete mutation
 * can invalidate the whole list without knowing which page is on screen.
 */
export const ADMIN_EMAIL_SUBSCRIBERS_KEY = [
  "admin",
  "email-subscribers",
] as const;

/** One row of the listing — exactly the three fields the jbuilder view renders. */
export interface AdminEmailSubscriber {
  id: string;
  email: string;
  createdAt: string;
}

/** A page of the listing, with the metadata pagy publishes as response headers. */
export interface EmailSubscriberPage {
  subscribers: AdminEmailSubscriber[];
  currentPage: number;
  totalPages: number;
  totalCount: number;
}

/** The envelope the jbuilder view renders. */
interface EmailSubscribersResponse {
  emailSubscribers?: AdminEmailSubscriber[];
}

/**
 * Reads one of pagy's header values as a positive integer.
 *
 * The fallback is not defensive decoration: a proxy that strips unknown
 * headers would otherwise put `NaN` on the page and disable both pagination
 * controls on a list that really does have more rows. Falling back to
 * something the body itself proves keeps the first page usable instead.
 */
function readCount(headers: Headers, name: string, fallback: number): number {
  const parsed = Number(headers.get(name));
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Fetch one page of subscribers, newest first (the server's order — this end
 * does no sorting of its own).
 *
 * `page` always rides along, including for page 1. Pagy would default to it,
 * but sending it makes the request the same shape at every page and the URL
 * the one thing that says which page is being asked for.
 *
 * Nothing is caught. The api-client throws `HttpError` on any status >= 400, so
 * a 401 from an expired operator session reaches the caller as a rejection it
 * can branch on — resolving an empty page instead would show the operator an
 * empty mailing list rather than a session that needs renewing.
 */
export async function fetchEmailSubscribers(
  page: number,
): Promise<EmailSubscriberPage> {
  const response = await apiClient.get<EmailSubscribersResponse>(
    `${ADMIN_SUBSCRIBERS_URL}?page=${page}`,
  );

  const subscribers = response.data.emailSubscribers ?? [];

  return {
    subscribers,
    currentPage: readCount(response.headers, "Current-Page", page),
    totalPages: readCount(response.headers, "Total-Pages", 1),
    totalCount: readCount(response.headers, "Total-Count", subscribers.length),
  };
}

/**
 * `fetchEmailSubscribers` as a TanStack query.
 *
 * Retries are off. The failure worth showing quickly is a 401, and retrying a
 * refused session three times only delays the message that would fix it.
 *
 * `keepPreviousData` holds the current page on screen while the next one loads,
 * so paging does not blank the table and shift the buttons out from under the
 * pointer.
 */
export function useEmailSubscribers(page: number) {
  return useQuery({
    queryKey: [...ADMIN_EMAIL_SUBSCRIBERS_KEY, page],
    queryFn: () => fetchEmailSubscribers(page),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/**
 * Remove one address from the list. The server hard-deletes the row — there is
 * no archive and no undo, which is why the page asks for a confirmation first.
 *
 * Resolves with nothing: the endpoint answers 204, and the fresh list that
 * follows the invalidation is the only state the page needs.
 */
export async function deleteEmailSubscriber(id: string): Promise<void> {
  await apiClient.delete(`${ADMIN_SUBSCRIBERS_URL}/${id}`);
}

/**
 * `deleteEmailSubscriber` as a TanStack mutation, refetching the list on
 * success.
 *
 * The prefix is invalidated rather than one page's key: a removal shifts every
 * later row up a page, so the page on screen is not the only one whose cached
 * copy just went stale.
 *
 * Retries are off, for a reason the query does not share — DELETE is applied on
 * the server before any failure the client sees, so an automatic retry could
 * chase a completed removal and turn it into the 404 the operator then reads as
 * an error.
 */
export function useDeleteEmailSubscriber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEmailSubscriber,
    retry: false,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ADMIN_EMAIL_SUBSCRIBERS_KEY }),
  });
}
